/**
 * This file includes polyfills needed by Angular and is loaded before the app.
 * You can add your own extra polyfills to this file.
 *
 * This file is divided into 2 sections:
 *   1. Browser polyfills. These are applied before loading ZoneJS and are sorted by browsers.
 *   2. Application imports. Files imported after ZoneJS that should be loaded before your main
 *      file.
 *
 * The current setup is for so-called "evergreen" browsers; the last versions of browsers that
 * automatically update themselves. This includes Safari >= 10, Chrome >= 55 (including Opera),
 * Edge >= 13 on the desktop, and iOS 10 and Chrome on mobile.
 *
 * Learn more in https://angular.io/guide/browser-support
 */

/***************************************************************************************************
 * BROWSER POLYFILLS
 */

/** IE11 requires the following for NgClass support on SVG elements */

/**
 * Web Animations `@angular/platform-browser/animations`
 * Only required if AnimationBuilder is used within the application and using IE/Edge or Safari.
 * Standard animation support in Angular DOES NOT require any polyfills (as of Angular 6.0).
 */

/**
 * By default, zone.js will patch all possible macroTask and DomEvents
 * user can disable parts of macroTask/DomEvents patch by setting following flags
 * because those flags need to be set before `zone.js` being loaded, and webpack
 * will put import in the top of bundle, so user need to create a separate file
 * in this directory (for example: zone-flags.ts), and put the following flags
 * into that file, and then add the following code before importing zone.js.
 * import './zone-flags';
 *
 * The flags allowed in zone-flags.ts are listed here.
 *
 * The following flags will work for all browsers.
 *
 * (window as any).__Zone_disable_requestAnimationFrame = true; // disable patch requestAnimationFrame
 * (window as any).__Zone_disable_on_property = true; // disable patch onProperty such as onclick
 * (window as any).__zone_symbol__UNPATCHED_EVENTS = ['scroll', 'mousemove']; // disable patch specified eventNames
 *
 *  in IE/Edge developer tools, the addEventListener will also be wrapped by zone.js
 *  with the following flag, it will bypass `zone.js` patch for IE/Edge
 *
 *  (window as any).__Zone_enable_cross_context_check = true;
 *
 */

import './zone-flags';

/***************************************************************************************************
 * Zone JS is required by default for Angular itself.
 */
import 'zone.js/dist/zone';  // Included with Angular CLI.


/***************************************************************************************************
 * APPLICATION IMPORTS
 */

/**
 * Runtime polyfill for the CSS `:has()` selector on old WebViews (Chrome < 105,
 * e.g. Android 5). The build rewrites `:has()` into attribute selectors via
 * `postcss-preset-env` (see postcss.config.js); this activates them at runtime
 * and keeps them in sync with DOM changes. No-op on WebViews with native
 * `:has()` support.
 */
import cssHasPseudo from 'css-has-pseudo/browser';

cssHasPseudo(document);

/**
 * Runtime CSS `@layer` flattener for old WebViews (Chrome < 99, e.g. Android 5)
 * that do not support cascade layers.
 *
 * Cascade layers are intentionally NOT flattened at build time (see
 * postcss.config.js) because that mutated the cascade for modern WebViews too
 * (a ~12k-selector `:not(#\#)` specificity boost in the single shipped bundle).
 * The emitted bundle therefore keeps native `@layer`; modern WebViews use it
 * natively, and this shim flattens it for old WebViews instead. It covers BOTH
 * layer sources these engines would otherwise silently discard:
 *   1. The emitted stylesheet `<link>` (app + statically-imported PrimeNG
 *      `saga-blue` theme / `primeng.min.css`).
 *   2. Runtime-injected `<style>` tags — PrimeNG injects each component's
 *      structural CSS as `@layer primeng { ... }` when its lazy module loads.
 * No-op on WebViews with native `@layer` support.
 */
if (typeof window !== 'undefined' && !('CSSLayerBlockRule' in window)) {
  /** Removes `@layer name { ... }` wrappers (and `@layer a, b;` statements), keeping inner rules in source order. */
  const unwrapLayers = (css: string): string => {
    let out = '';
    let i = 0;
    while (i < css.length) {
      const at = css.indexOf('@layer', i);
      if (at === -1) {
        out += css.slice(i);
        break;
      }
      out += css.slice(i, at);
      let j = at + '@layer'.length;
      while (j < css.length && css[j] !== '{' && css[j] !== ';') {
        j++;
      }
      if (css[j] === ';') {
        // `@layer a, b;` ordering statement — drop it entirely.
        i = j + 1;
        continue;
      }
      // `@layer name { ... }` block — keep inner content, drop the wrapper.
      let depth = 1;
      let k = j + 1;
      while (k < css.length && depth > 0) {
        if (css[k] === '{') {
          depth++;
        } else if (css[k] === '}') {
          depth--;
        }
        k++;
      }
      out += unwrapLayers(css.slice(j + 1, k - 1));
      i = k;
    }
    return out;
  };

  /** Flattens a single <style> element in place when it contains `@layer`. */
  const flattenStyle = (el: HTMLStyleElement): void => {
    const css = el.textContent ?? '';
    if (css.indexOf('@layer') === -1) {
      return;
    }
    const flat = unwrapLayers(css);
    if (flat !== css) {
      el.textContent = flat;
    }
  };

  document.querySelectorAll('style').forEach(el => flattenStyle(el));

  /**
   * Flattens the emitted stylesheet `<link>` bundle(s), which the runtime
   * `<style>` shim above cannot reach. Fetches each same-origin stylesheet,
   * unwraps its `@layer` blocks, injects the result as a plain `<style>` in the
   * link's original cascade position, then disables the link so the bundle is
   * not applied twice. Fetch runs before Angular paints the app UI (app-root is
   * still empty at polyfill time), so old WebViews do not flash broken layout.
   */
  const flattenLinkedStylesheets = (): void => {
    if (typeof fetch !== 'function') {
      return;
    }
    const links = Array.from(
      document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'),
    );
    links.forEach(link => {
      const href = link.href;
      if (!href) {
        return;
      }
      fetch(href, { credentials: 'same-origin' })
        .then(res => res.text())
        .then(css => {
          if (css.indexOf('@layer') === -1) {
            return;
          }
          const style = document.createElement('style');
          style.textContent = unwrapLayers(css);
          link.parentNode?.insertBefore(style, link);
          // Neutralise the original link so its (partly discarded) layered
          // copy does not double-apply alongside the flattened <style>.
          link.media = 'not all';
          link.disabled = true;
        })
        .catch(() => {
          // Leave the original link in place if the fetch fails — better a
          // partially-styled bundle than an unstyled app on these devices.
          // Sentry is not yet initialised this early, so mark for debugging.
          link.dataset['layerFlattenFailed'] = 'true';
        });
    });
  };

  flattenLinkedStylesheets();

  const observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach(node => {
        if (node.nodeName === 'STYLE') {
          flattenStyle(node as HTMLStyleElement);
        }
      });
    }
  });
  observer.observe(document.head, { childList: true });
}
